/**
 * Container Selection Step
 *
 * Material-first selection flow: Select material type first, then show
 * appropriate container sizes based on material weight/type restrictions.
 *
 * PRRO Integration:
 * - Shows PricingModelSelector when provider has PRRO enabled and multiple models
 * - Triggers MaterialContaminationWarning for C&D materials with treated wood
 * - Adds treated wood checkbox for C&D materials
 */

import { useState, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useProviderConfig } from '../../../contexts/ProviderConfigContext';
import { ContainerSizeSelector, type ContainerSize } from '../../../components/order/ContainerSizeSelector';
import { InfoIcon, PackageIcon, AlertTriangleIcon, CheckCircle2Icon, ChevronDownIcon, AlertCircleIcon } from 'lucide-react';
import type { OrderForm } from '../../../schemas/order.schema';
import { socialProofService } from '../../../services/social-proof.service';
import { SocialProofBadge } from '../../../components/social/SocialProofBadge';
import { DeliveryTimeIndicator } from '../../../components/delivery/DeliveryTimeIndicator';
import { calculateDeliveryEstimate } from '../../../lib/utils/delivery-time';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../../components/ui/sheet';
import { ContainerComparator } from '../../../components/comparison/ContainerComparator';
import { useMediaQuery } from '../../../hooks/use-media-query';
import { MaterialRules, type ContainerSize as DomainContainerSize } from '../../../lib/domain/material-rules';
import { FieldError } from '../../../components/widget/FieldError';
import { PricingModelSelector, type PricingModelOption } from '../../../components/pricing/PricingModelSelector';
import { MaterialContaminationWarning } from '../../../components/pricing/MaterialContaminationWarning';
import { RealTimePricing } from '../../../components/pricing/RealTimePricing';
import { Checkbox } from '../../../components/ui/checkbox';
import { Label } from '../../../components/ui/label';

// Common material types with categories
const MATERIAL_CATEGORIES = [
  {
    category: 'Heavy Materials (10-yard only)',
    materials: [
      { value: 'concrete', label: 'Concrete Only', weight: 'heavy' },
      { value: 'asphalt', label: 'Asphalt Only', weight: 'heavy' },
      { value: 'dirt_soil', label: 'Dirt & Soil', weight: 'heavy' },
      { value: 'rock_gravel', label: 'Rock & Gravel', weight: 'heavy' },
    ],
  },
  {
    category: 'Construction & Demolition',
    materials: [
      { value: 'mixed_cd', label: 'Mixed Construction & Demolition', popular: true, weight: 'cd' },
      { value: 'wood', label: 'Wood & Lumber', weight: 'cd' },
      { value: 'drywall', label: 'Drywall', weight: 'cd' },
      { value: 'roofing', label: 'Roofing Materials', weight: 'cd' },
    ],
  },
  {
    category: 'Landscaping',
    materials: [
      { value: 'green_waste', label: 'Green Waste (Yard Debris)', weight: 'light' },
    ],
  },
  {
    category: 'General',
    materials: [
      { value: 'household', label: 'Household Junk', popular: true, weight: 'light' },
      { value: 'furniture', label: 'Furniture', weight: 'light' },
      { value: 'mixed_waste', label: 'Mixed General Waste', weight: 'light' },
    ],
  },
  {
    category: 'Recyclables',
    materials: [
      { value: 'metal', label: 'Scrap Metal', weight: 'light' },
      { value: 'cardboard', label: 'Cardboard', weight: 'light' },
      { value: 'mixed_recycling', label: 'Mixed Recyclables', weight: 'light' },
    ],
  },
];

/**
 * Get material info by value
 */
function getMaterialInfo(materialValue: string) {
  for (const category of MATERIAL_CATEGORIES) {
    const material = category.materials.find(m => m.value === materialValue);
    if (material) {
      return { ...material, category: category.category };
    }
  }
  return null;
}

export function ContainerSelectionStep() {
  const { setValue, watch, formState: { errors } } = useFormContext<OrderForm>();
  const { config } = useProviderConfig();
  const { providerSlug } = useParams<{ providerSlug: string }>();

  const selectedSize = watch('containerSize');
  const selectedMaterial = watch('materialType');
  const pricingModelId = watch('pricingModelId');
  const hasTreatedWood = watch('hasTreatedWood');

  // Track if material section is collapsed
  const [isMaterialCollapsed, setIsMaterialCollapsed] = useState(false);

  // Track comparison modal state
  const [showComparison, setShowComparison] = useState(false);
  const isDesktop = useMediaQuery('(min-width: 768px)');

  // PRRO state management
  const [showContaminationWarning, setShowContaminationWarning] = useState(false);

  // Feature detection: Check if provider has PRRO enabled
  // TODO: Update when ProviderConfig includes prroEnabled field
  const isPRROEnabled = false; // (config as any)?.features?.prroEnabled ?? false;

  // Mock PRRO pricing models for demonstration
  // TODO: Replace with API call when backend is ready
  const pricingModels = useMemo<PricingModelOption[]>(() => {
    if (!isPRROEnabled || !selectedMaterial || !selectedSize) return [];

    return [
      {
        id: 'flat_rate',
        name: 'Standard Flat Rate',
        shortDescription: 'All-inclusive pricing with included tonnage',
        detailedDescription: 'Predictable flat rate includes container rental, delivery, pickup, and up to 4 tons of disposal. Perfect for most projects where you want to know your exact cost upfront.',
        benefits: [
          'Predictable costs with no surprises',
          'Includes up to 4 tons of material disposal',
          'Simple, transparent billing',
          'Best for standard construction projects'
        ],
        bestFor: [
          'Typical C&D projects',
          'First-time customers',
          'Budget-conscious projects',
          'Standard material volumes'
        ],
        example: {
          basePrice: 45000, // $450 in cents
          includedTonnage: 4,
        },
        recommended: true,
        popularityPercent: 75,
      },
      {
        id: 'exact_tonnage',
        name: 'Pay-As-You-Throw',
        shortDescription: 'Pay only for the material you dispose',
        detailedDescription: 'Lower base rate plus per-ton disposal charges. Ideal for projects with uncertain material volumes or very light loads. You only pay for what you actually throw away.',
        benefits: [
          'Lower upfront cost',
          'Pay only for actual disposal',
          'Better for light loads',
          'Flexible for uncertain volumes'
        ],
        bestFor: [
          'Light material loads (< 2 tons)',
          'Uncertain project scope',
          'Cost-conscious projects',
          'Mixed material types'
        ],
        example: {
          basePrice: 15000, // $150 in cents
          perTonRate: 7500, // $75/ton in cents
        },
        popularityPercent: 20,
      },
    ];
  }, [isPRROEnabled, selectedMaterial, selectedSize]);

  // Get material info
  const materialInfo = selectedMaterial ? getMaterialInfo(selectedMaterial) : null;

  // Get social proof data for selected material
  const popularContainer = useMemo(() => {
    if (!selectedMaterial || !config?.socialProofSettings) return null;
    return socialProofService.getPopularContainer(
      selectedMaterial,
      config.socialProofSettings
    );
  }, [selectedMaterial, config?.socialProofSettings]);

  const materialUsage = useMemo(() => {
    if (!selectedMaterial || !config?.socialProofSettings) return null;
    return socialProofService.getMaterialUsage(
      selectedMaterial,
      config.socialProofSettings
    );
  }, [selectedMaterial, config?.socialProofSettings]);

  // Calculate delivery estimate based on provider settings
  const deliveryEstimate = calculateDeliveryEstimate(config?.deliveryTimeSettings);

  // Filter container sizes based on selected material
  const availableContainers: ContainerSize[] = useMemo(() => {
    // Get allowed sizes from domain layer
    const allowedSizes = selectedMaterial ? MaterialRules.getAllowedSizes(selectedMaterial) : [];

    return config?.services
      .filter(s => s.available)
      .filter(s => !selectedMaterial || allowedSizes.includes(s.containerSize as DomainContainerSize))
      .map(service => {
        const sizeNumber = parseInt(service.containerSize);

        // Determine if this container has social proof
        let socialProof = undefined;
        if (popularContainer && service.containerSize === popularContainer.popularSize) {
          // Show popularity badge for the most popular size
          socialProof = {
            type: 'popularity' as const,
            value: `${popularContainer.percentage}% choose this`,
          };
        }

        return {
          id: service.containerSize,
          size: service.containerSize,
          description: service.description,
          dimensions: {
            length: sizeNumber === 10 ? 12 : sizeNumber === 20 ? 16 : sizeNumber === 30 ? 20 : 22,
            width: 8,
            height: sizeNumber === 10 ? 4 : sizeNumber === 20 ? 4.5 : sizeNumber === 30 ? 5 : 6,
          },
          capacity: {
            tons: sizeNumber === 10 ? '2-3' : sizeNumber === 20 ? '3-4' : sizeNumber === 30 ? '4-5' : '5-6',
            truckLoads: sizeNumber === 10 ? '3-4' : sizeNumber === 20 ? '6-8' : sizeNumber === 30 ? '10-12' : '12-15',
            cubicYards: sizeNumber,
          },
          commonUses:
            sizeNumber === 10 ? ['Small bathroom remodel', 'Garage cleanout', 'Heavy materials (concrete, dirt)'] :
            sizeNumber === 20 ? ['Kitchen renovation', 'Large basement cleanout', 'Roof replacement'] :
            sizeNumber === 30 ? ['Whole house cleanout', 'Major renovation', 'Commercial project'] :
            ['Large construction project', 'Complete demolition', 'Commercial cleanout'],
          price: config.pricing?.basePricing?.[service.containerSize] ? `$${config.pricing.basePricing[service.containerSize]}` : undefined,
          available: true,
          recommended: service.containerSize === '20Y',
          socialProof,
        };
      }) || [];
  }, [config?.services, config?.pricing, selectedMaterial, popularContainer]);

  const handleMaterialSelect = (value: string) => {
    setValue('materialType', value);

    // Clear container selection if it's no longer valid for the new material
    if (selectedSize) {
      const newAllowedSizes = MaterialRules.getAllowedSizes(value);
      if (!newAllowedSizes.includes(selectedSize as DomainContainerSize)) {
        setValue('containerSize', '');
      }
    }

    // Collapse material section after selection
    setIsMaterialCollapsed(true);
  };

  const handleSizeSelect = (sizeId: string) => {
    setValue('containerSize', sizeId);
  };

  const handleCompareClick = () => {
    setShowComparison(true);
  };

  const handleCompareSelect = (container: ContainerSize) => {
    setValue('containerSize', container.id);
    setShowComparison(false);
  };

  // PRRO: Handle treated wood checkbox change
  const handleTreatedWoodChange = (checked: boolean) => {
    if (checked) {
      // Show contamination warning modal
      setShowContaminationWarning(true);
    } else {
      // Clear treated wood flag
      setValue('hasTreatedWood', false);
      setValue('contaminationAcknowledged', false);
    }
  };

  // PRRO: Handle contamination warning acknowledgment
  const handleContaminationAcknowledge = (acknowledged: boolean) => {
    if (acknowledged) {
      setValue('hasTreatedWood', true);
      setValue('contaminationAcknowledged', true);
      // Add to acknowledged terms
      const currentTerms = watch('acknowledgedTerms') || [];
      if (!currentTerms.includes('contamination')) {
        setValue('acknowledgedTerms', [...currentTerms, 'contamination']);
      }
    }
  };

  // PRRO: Handle pricing model selection
  const handlePricingModelSelect = (modelId: string) => {
    setValue('pricingModelId', modelId);
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-50 rounded-lg">
            <PackageIcon className="h-6 w-6 text-primary-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900">
            Select Your Container
          </h2>
        </div>
        <p className="text-lg text-gray-600 leading-relaxed">
          First, tell us what material you&apos;ll be disposing. This helps us show you the right container sizes.
        </p>
      </div>

      {/* Material Type Selection */}
      <div className="bg-white rounded-lg border-2 border-gray-200 overflow-hidden">
        {/* Material Header - Collapsible */}
        <button
          type="button"
          onClick={() => setIsMaterialCollapsed(!isMaterialCollapsed)}
          className="w-full min-h-[56px] p-5 flex items-center justify-between hover:bg-gray-50 active:bg-gray-100 transition-colors touch-manipulation"
          disabled={!selectedMaterial}
        >
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${selectedMaterial ? 'bg-green-100' : 'bg-gray-100'}`}>
              {selectedMaterial ? (
                <CheckCircle2Icon className="h-5 w-5 text-green-600" />
              ) : (
                <AlertTriangleIcon className="h-5 w-5 text-gray-600" />
              )}
            </div>
            <div className="text-left">
              <h3 className="text-lg font-semibold text-gray-900">
                {selectedMaterial ? 'Material Type Selected' : 'Step 1: Select Material Type'}
              </h3>
              {selectedMaterial && (
                <p className="text-sm text-gray-600">
                  {materialInfo?.label}
                </p>
              )}
            </div>
          </div>
          {selectedMaterial && (
            <ChevronDownIcon
              className={`h-5 w-5 text-gray-400 transition-transform ${isMaterialCollapsed ? '' : 'rotate-180'}`}
            />
          )}
        </button>

        {/* Material Selection Form - Collapsible */}
        {!isMaterialCollapsed && (
          <div className="p-5 pt-0 space-y-4 border-t border-gray-200">
            <div>
              <label htmlFor="material-type" className="block text-sm font-semibold text-gray-700 mb-2">
                What material will you be disposing? <span className="text-red-500">*</span>
              </label>
              <select
                id="material-type"
                value={selectedMaterial}
                onChange={(e) => handleMaterialSelect(e.target.value)}
                className="w-full min-h-[48px] px-4 py-3 text-[16px] border border-gray-300 rounded-lg shadow-sm transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 hover:border-gray-400 appearance-none bg-white"
                aria-required="true"
                aria-invalid={errors.materialType ? "true" : "false"}
                aria-describedby={errors.materialType ? "material-error" : undefined}
              >
                <option value="">Choose material type...</option>
                {MATERIAL_CATEGORIES.map((category) => (
                  <optgroup key={category.category} label={category.category}>
                    {category.materials.map((material) => (
                      <option key={material.value} value={material.value}>
                        {material.label}
                        {material.popular ? ' (Popular)' : ''}
                      </option>
                    ))}
                  </optgroup>
                ))}
                <option value="other">Other / Not Sure</option>
              </select>

              <FieldError id="material-error" message={errors.materialType?.message} />

              {/* Visual Error State for Material Selection */}
              {errors.materialType && (
                <div className="border-2 border-red-500 rounded-lg p-4 bg-red-50 mt-2">
                  <div className="flex items-start gap-2">
                    <AlertCircleIcon className="h-5 w-5 text-red-600 flex-shrink-0" />
                    <p className="text-sm text-red-600 font-medium">{errors.materialType.message}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Material Type Info Box */}
            {selectedMaterial && MaterialRules.isHeavyMaterial(selectedMaterial) && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <InfoIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-blue-900 mb-1">
                      Heavy Material Restriction
                    </p>
                    <p className="text-sm text-blue-800">
                      {materialInfo?.label} is too heavy for larger containers. Only 10-yard containers are available for this material to ensure safe weight limits.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {selectedMaterial && MaterialRules.isCDMaterial(selectedMaterial) && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <InfoIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-blue-900 mb-1">
                      C&D Material
                    </p>
                    <p className="text-sm text-blue-800">
                      Construction & demolition materials work best in 20-yard or larger containers. We reserve 10-yard containers for heavy materials like concrete and dirt.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* PRRO: Treated Wood Checkbox (C&D materials only) */}
            {isPRROEnabled && selectedMaterial && MaterialRules.isCDMaterial(selectedMaterial) && (
              <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="treated-wood"
                    checked={hasTreatedWood || false}
                    onChange={(e) => handleTreatedWoodChange(e.target.checked)}
                    className="mt-0.5"
                  />
                  <div className="flex-1">
                    <Label
                      htmlFor="treated-wood"
                      className="text-sm font-medium text-gray-900 cursor-pointer"
                    >
                      My load includes pressure-treated wood
                    </Label>
                    <p className="text-xs text-gray-600 mt-1">
                      This includes deck boards, fence posts, or any wood with a greenish tint. Different disposal rates apply.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Material Usage Social Proof */}
            {selectedMaterial && materialUsage && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-3 flex-wrap">
                  <SocialProofBadge
                    type="popularity"
                    value={`${materialUsage.usageCount} customers`}
                    size="sm"
                    showTooltip={false}
                  />
                  <p className="text-sm text-green-800">
                    chose this material this month
                  </p>
                  {materialUsage.trending && (
                    <SocialProofBadge
                      type="trending"
                      size="sm"
                      showTooltip={false}
                    />
                  )}
                </div>
              </div>
            )}

            {/* Prohibited Items Warning */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertTriangleIcon className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-yellow-900 mb-2">
                    Prohibited Items
                  </p>
                  <p className="text-xs text-yellow-800 mb-2">
                    We cannot accept hazardous materials including:
                  </p>
                  <div className="grid grid-cols-2 gap-1 text-xs text-yellow-800">
                    <div className="flex items-start">
                      <span className="block w-1 h-1 rounded-full bg-yellow-600 mt-1.5 mr-1.5 flex-shrink-0"></span>
                      <span>Paint & chemicals</span>
                    </div>
                    <div className="flex items-start">
                      <span className="block w-1 h-1 rounded-full bg-yellow-600 mt-1.5 mr-1.5 flex-shrink-0"></span>
                      <span>Asbestos</span>
                    </div>
                    <div className="flex items-start">
                      <span className="block w-1 h-1 rounded-full bg-yellow-600 mt-1.5 mr-1.5 flex-shrink-0"></span>
                      <span>Batteries</span>
                    </div>
                    <div className="flex items-start">
                      <span className="block w-1 h-1 rounded-full bg-yellow-600 mt-1.5 mr-1.5 flex-shrink-0"></span>
                      <span>Tires & fluids</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Container Size Selection - Only show after material is selected */}
      {selectedMaterial && (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${selectedSize ? 'bg-green-100' : 'bg-gray-100'}`}>
              {selectedSize ? (
                <CheckCircle2Icon className="h-5 w-5 text-green-600" />
              ) : (
                <PackageIcon className="h-5 w-5 text-gray-600" />
              )}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Step 2: Choose Container Size <span className="text-red-500" aria-label="required">*</span>
              </h3>
              <p className="text-sm text-gray-600">
                {availableContainers.length === 1 && availableContainers[0]
                  ? `Based on your material, only ${availableContainers[0].size} is available.`
                  : `${availableContainers.length} sizes available for ${materialInfo?.label}.`
                }
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-1">
            <ContainerSizeSelector
              sizes={availableContainers}
              selectedSize={selectedSize || null}
              onSelect={handleSizeSelect}
              showPricing={!!config?.pricing}
              showComparison={availableContainers.length > 1}
              onCompare={handleCompareClick}
            />
          </div>

          <FieldError id="container-error" message={errors.containerSize?.message} />

          {/* Visual Error State for Container Selection */}
          {errors.containerSize && (
            <div className="border-2 border-red-500 rounded-lg p-4 bg-red-50">
              <div className="flex items-start gap-2">
                <AlertCircleIcon className="h-5 w-5 text-red-600 flex-shrink-0" />
                <p className="text-sm text-red-600 font-medium">{errors.containerSize.message}</p>
              </div>
            </div>
          )}

          {/* Delivery Time Indicator - Show prominently when container is selected */}
          {selectedSize && config?.deliveryTimeSettings?.enabled && (
            <div className="animate-in fade-in-0 slide-in-from-top-4 duration-300">
              <DeliveryTimeIndicator
                estimatedDelivery={deliveryEstimate}
                variant="subtle"
                showRushOption={false}
              />
            </div>
          )}

          {/* PRRO: Pricing Model Selector - Show after container size is selected */}
          {isPRROEnabled && selectedSize && selectedMaterial && pricingModels.length > 1 && (
            <div className="animate-in fade-in-0 slide-in-from-top-4 duration-300 mt-6">
              <PricingModelSelector
                models={pricingModels}
                selectedModel={(pricingModelId as 'flat_rate' | 'exact_tonnage' | 'specialty') || null}
                onSelect={handlePricingModelSelect}
                materialType={materialInfo?.label}
              />
            </div>
          )}

          {/* Real-time Pricing Display - Show after container size is selected */}
          {selectedSize && selectedMaterial && providerSlug && (
            <div className="animate-in fade-in-0 slide-in-from-top-4 duration-300 mt-6">
              <RealTimePricing
                providerSlug={providerSlug}
                params={{
                  containerSize: selectedSize,
                  materialType: selectedMaterial,
                  pricingModelId,
                  hasTreatedWood,
                }}
              />
            </div>
          )}
        </div>
      )}

      {/* Prompt to select material if not selected */}
      {!selectedMaterial && (
        <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
          <PackageIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-base text-gray-600">
            Please select a material type above to see available container sizes.
          </p>
        </div>
      )}

      {/* Comparison Modal (Desktop) / Sheet (Mobile) */}
      {isDesktop ? (
        <Dialog open={showComparison} onOpenChange={setShowComparison}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Container Size Comparison</DialogTitle>
            </DialogHeader>
            <ContainerComparator
              containers={availableContainers}
              selectedContainerId={selectedSize || null}
              materialType={materialInfo?.label}
              onSelect={handleCompareSelect}
              maxComparisons={4}
            />
          </DialogContent>
        </Dialog>
      ) : (
        <Sheet open={showComparison} onOpenChange={setShowComparison}>
          <SheetContent side="bottom" className="h-[90vh] overflow-y-auto">
            <SheetHeader>
              <SheetTitle>Container Size Comparison</SheetTitle>
            </SheetHeader>
            <div className="mt-4">
              <ContainerComparator
                containers={availableContainers}
                selectedContainerId={selectedSize || null}
                materialType={materialInfo?.label}
                onSelect={handleCompareSelect}
                maxComparisons={4}
              />
            </div>
          </SheetContent>
        </Sheet>
      )}

      {/* PRRO: Material Contamination Warning Modal */}
      {isPRROEnabled && selectedMaterial && MaterialRules.isCDMaterial(selectedMaterial) && (
        <MaterialContaminationWarning
          open={showContaminationWarning}
          onOpenChange={setShowContaminationWarning}
          materialType={selectedMaterial}
          materialLabel={materialInfo?.label || 'C&D Material'}
          contaminationType="treated_wood"
          costComparison={{
            withoutContamination: 71716, // $717.16 in cents
            withContamination: 156500, // $1,565 in cents
            tonneRate: 7500, // $75/ton in cents
            contaminationRate: 15000, // $150/ton in cents
          }}
          recoveryOptions={{
            separateContainers: true,
            exactTonnagePricing: true,
          }}
          onAcknowledge={handleContaminationAcknowledge}
          onSelectRecovery={(option) => {
            // Handle recovery option selection
            if (option === 'exact_tonnage') {
              // Switch to exact tonnage pricing model
              const exactTonnageModel = pricingModels.find(m => m.id === 'exact_tonnage');
              if (exactTonnageModel) {
                setValue('pricingModelId', 'exact_tonnage');
              }
            } else if (option === 'separate') {
              // Clear treated wood flag and close modal
              setValue('hasTreatedWood', false);
              setShowContaminationWarning(false);
              // TODO: Trigger adding another container to cart
            } else if (option === 'continue') {
              // Already handled by onAcknowledge
              setShowContaminationWarning(false);
            }
          }}
        />
      )}
    </div>
  );
}
